import {
  createErrorMessage,
  createValidationErrorMessage,
  mqttLogger,
} from '@hooks/mqtt/utils';
import { MqttClient } from 'mqtt';
import { MqttMessage } from '../types';

export function createMqttMessageFromJson(
  jsonPayload: any,
  topic: string,
): MqttMessage {
  return {
    value: jsonPayload.value ?? jsonPayload,
    topic,
    timestamp: jsonPayload.ts ?? Date.now(),
  };
}

export function createRawMqttMessage(
  payload: string,
  topic: string,
): MqttMessage {
  return {
    value: payload,
    topic,
    timestamp: Date.now(),
  };
}

export async function parsePayloadToMessage(
  topic: string,
  payload: Buffer,
  config?: {
    parseJson?: boolean;
    validator?: (data: any) => boolean | Promise<boolean>;
    transform?: (message: MqttMessage) => MqttMessage | Promise<MqttMessage>;
  },
): Promise<MqttMessage> {
  let message: MqttMessage;

  if (config?.parseJson !== false) {
    try {
      const jsonPayload = JSON.parse(payload.toString());

      if (config?.validator) {
        const isValid = await config.validator(jsonPayload);
        if (!isValid) {
          message = createValidationErrorMessage(topic);
        } else {
          message = createMqttMessageFromJson(jsonPayload, topic);
        }
      } else {
        message = createMqttMessageFromJson(jsonPayload, topic);
      }
    } catch (parseError) {
      message = createErrorMessage(topic, parseError);
    }
  } else {
    message = createRawMqttMessage(payload.toString(), topic);
  }

  if (config?.transform) {
    message = await config.transform(message);
  }

  return message;
}

export const createMessageHandler = (
  getActiveTopics: () => Set<string>,
  handleMessage: (params: { topic: string; payload: Buffer }) => void,
) => {
  return (topic: string, payload: Buffer) => {
    const activeTopics = getActiveTopics();
    if (activeTopics.has(topic)) {
      handleMessage({ topic, payload });
      mqttLogger.debug(`Routed message for topic: ${topic}`);
    }
  };
};

export const initializeMessageListener = (
  client: MqttClient,
  messageHandler: (topic: string, payload: Buffer) => void,
) => {
  const beforeCount = client.listenerCount('message');
  client.on('message', messageHandler);
  const afterCount = client.listenerCount('message');

  if (afterCount !== beforeCount + 1) {
    mqttLogger.error(
      `Handler registration failed: expected ${beforeCount + 1} listeners, got ${afterCount}`,
    );
    throw new Error(
      `MQTT handler registration failed: expected ${beforeCount + 1} listeners, got ${afterCount}`,
    );
  }

  mqttLogger.info(
    `MQTT message listener initialized (total listeners: ${afterCount})`,
  );
};

export const cleanupMessageListener = (
  client: MqttClient | null,
  handler: ((topic: string, payload: Buffer) => void) | null,
) => {
  mqttLogger.info('Cleaning up MQTT message listener...');

  if (!client) {
    mqttLogger.warn('MQTT client not available during listener cleanup');
    return;
  }
  if (!handler) {
    mqttLogger.warn('No message handler found during listener cleanup');
    return;
  }

  const beforeCount = client.listenerCount('message');
  client.removeListener('message', handler);
  const afterCount = client.listenerCount('message');

  if (afterCount !== beforeCount - 1) {
    mqttLogger.error(
      `Handler removal failed: expected ${beforeCount - 1} listeners, got ${afterCount}`,
    );
    throw new Error(
      `MQTT handler removal failed: expected ${beforeCount - 1} listeners, got ${afterCount}`,
    );
  }

  mqttLogger.info(
    `MQTT message listener removed (remaining listeners: ${afterCount})`,
  );
};

export const incrementListenerRefCount = (currentCount: number): number => {
  const newCount = currentCount + 1;
  mqttLogger.debug(`Listener ref count: ${newCount}`);
  return newCount;
};

export const decrementListenerRefCount = (currentCount: number): number => {
  const newCount = Math.max(0, currentCount - 1);
  mqttLogger.debug(`Listener ref count: ${newCount}`);
  return newCount;
};

export const canInitializeListener = (
  isListenerActive: boolean,
  isConnected: boolean,
  client: MqttClient | null,
): boolean => {
  if (isListenerActive) {
    mqttLogger.debug('MQTT listener already active, skipping initialization');
    return false;
  }

  if (!client) {
    mqttLogger.warn('Cannot initialize listener: MQTT client is null');
    return false;
  }

  if (!isConnected) {
    mqttLogger.warn('Cannot initialize listener: MQTT client is not connected');
    return false;
  }

  const currentListeners = client.listenerCount('message');
  mqttLogger.debug(`Current message listeners count: ${currentListeners}`);

  return true;
};

export const canCleanupListener = (isListenerActive: boolean): boolean => {
  if (!isListenerActive) {
    mqttLogger.debug('MQTT listener already inactive');
    return false;
  }

  return true;
};
